import {
  isZero,
  type Page,
  type SupplierCreditSourceType,
  type SupplierRefund,
  type SupplierRefundInput,
  type SupplierRefundListItem,
  type SupplierRefundSummary,
  type SupplierReturnListItem,
} from '@sahab/contracts'
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type DownloadedFile } from '../../lib/api'
import type { Language } from '../../lib/format'
import { invalidatePurchasing, purchasingKeys, type SupplierPaymentListItem } from './queries'
import type { RefundSummaryParams, SupplierRefundListParams } from './search'

/**
 * Supplier refunds live under the purchasing keys: a refund changes the payments and returns it
 * draws on, the supplier's statement and the aging.
 */
export const supplierRefundKeys = {
  all: [...purchasingKeys.all, 'refunds'] as const,
  list: (query: SupplierRefundListParams) => [...purchasingKeys.all, 'refunds', 'list', query] as const,
  summary: (query: RefundSummaryParams) => [...purchasingKeys.all, 'refunds', 'summary', query] as const,
  detail: (id: string) => [...purchasingKeys.all, 'refunds', 'detail', id] as const,
  credits: (supplierId: string) => [...purchasingKeys.all, 'refunds', 'credits', supplierId] as const,
}

/** The largest page the API serves: the newest payments and returns of one supplier with credit left. */
export const SUPPLIER_CREDITS_LIMIT = 200

const path = (id: string, action?: string) =>
  `/purchasing/refunds/${encodeURIComponent(id)}${action ? `/${action}` : ''}`

export function supplierRefundListQueryOptions(query: SupplierRefundListParams) {
  return queryOptions({
    queryKey: supplierRefundKeys.list(query),
    queryFn: ({ signal }) =>
      api.get<Page<SupplierRefundListItem>>('/purchasing/refunds', {
        signal,
        query: { page: query.page, pageSize: query.pageSize, ...refundFilters(query) },
      }),
    placeholderData: keepPreviousData,
  })
}

function refundFilters(query: SupplierRefundListParams) {
  return {
    sort: query.sort,
    q: query.q,
    status: query.status,
    supplierId: query.supplierId,
    method: query.method,
    treasuryAccountId: query.treasuryAccountId,
    from: query.from,
    to: query.to,
  }
}

export function supplierRefundSummaryQueryOptions(query: RefundSummaryParams) {
  return queryOptions({
    queryKey: supplierRefundKeys.summary(query),
    queryFn: ({ signal }) => api.get<SupplierRefundSummary>('/purchasing/refunds/summary', { signal, query }),
    placeholderData: keepPreviousData,
  })
}

export function exportSupplierRefunds(
  query: SupplierRefundListParams,
  language: Language,
): Promise<DownloadedFile> {
  return api.download('/purchasing/refunds/export', {
    query: { format: 'csv', lang: language, ...refundFilters(query) },
    fallbackName: 'supplier-refunds.csv',
  })
}

export function supplierRefundQueryOptions(id: string) {
  return queryOptions({
    queryKey: supplierRefundKeys.detail(id),
    queryFn: ({ signal }) => api.get<SupplierRefund>(path(id), { signal }),
  })
}

/** Credit held with a supplier: a payment's advance or a return not applied to purchases. */
export interface SupplierCredit {
  sourceType: SupplierCreditSourceType
  id: string
  number: string
  date: string
  total: string
  refunded: string
  /** What is left: total − applied to purchases − already refunded. */
  available: string
}

export interface SupplierCredits {
  credits: SupplierCredit[]
  /** More posted payments or returns exist than one page holds (the oldest are not listed). */
  truncated: boolean
}

/**
 * The supplier's credit with us: posted payments with an unapplied advance and posted returns or
 * allowances not applied to purchases or refunded (the API's `hasOpenCredit` filter), oldest first.
 */
export function supplierCreditsQueryOptions(supplierId: string) {
  return queryOptions({
    queryKey: supplierRefundKeys.credits(supplierId),
    queryFn: async ({ signal }): Promise<SupplierCredits> => {
      const [payments, returns] = await Promise.all([
        api.get<Page<SupplierPaymentListItem>>('/purchasing/payments', {
          signal,
          query: { supplierId, hasOpenCredit: true, pageSize: SUPPLIER_CREDITS_LIMIT, sort: '-paymentDate' },
        }),
        api.get<Page<SupplierReturnListItem>>('/purchasing/returns', {
          signal,
          query: { supplierId, hasOpenCredit: true, pageSize: SUPPLIER_CREDITS_LIMIT, sort: '-returnDate' },
        }),
      ])
      const credits: SupplierCredit[] = [
        ...payments.items.map((p) => ({
          sourceType: 'PAYMENT' as const,
          id: p.id,
          number: p.number,
          date: p.paymentDate,
          total: p.amount,
          refunded: p.refunded,
          available: p.unallocated,
        })),
        ...returns.items.map((r) => ({
          sourceType: 'SUPPLIER_RETURN' as const,
          id: r.id,
          number: r.number ?? '',
          date: r.returnDate,
          total: r.total,
          refunded: r.refunded,
          available: r.unallocated,
        })),
      ].filter((c) => !isZero(c.available) && !c.available.startsWith('-'))
      credits.sort((a, b) => a.date.localeCompare(b.date) || a.number.localeCompare(b.number))
      return {
        credits,
        truncated: payments.total > payments.items.length || returns.total > returns.items.length,
      }
    },
  })
}

export function useCreateSupplierRefund() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: SupplierRefundInput) => api.post<SupplierRefund>('/purchasing/refunds', input),
    onSuccess: (refund) => {
      queryClient.setQueryData(supplierRefundKeys.detail(refund.id), refund)
      return invalidatePurchasing(queryClient, { ledger: true })
    },
  })
}

export function useVoidSupplierRefund(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) => api.post<SupplierRefund>(path(id, 'void'), { reason }),
    onSuccess: (refund) => {
      queryClient.setQueryData(supplierRefundKeys.detail(refund.id), refund)
      return invalidatePurchasing(queryClient, { ledger: true })
    },
  })
}
